import { useEffect, useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { sendTelegramSignal } from "../services/telegramService";

interface FearGreedProps {
  height?: number;
}

interface FearGreedData {
  timestamp: string;
  value: number;
  upper?: number;
  lower?: number;
}

const calculateBollingerBands = (data: FearGreedData[], period: number = 20, multiplier: number = 2) => {
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1).map(d => d.value);
    const sma = slice.reduce((a, b) => a + b, 0) / period;
    const variance = slice.reduce((a, b) => a + Math.pow(b - sma, 2), 0) / period;
    const std = Math.sqrt(variance);
    data[i].upper = sma + multiplier * std;
    data[i].lower = sma - multiplier * std;
  }
};

const aggregateToWeekly = (dailyData: FearGreedData[]): FearGreedData[] => {
  const weeklyData: FearGreedData[] = [];
  const dataByWeek: { [key: string]: number[] } = {};

  // Group data by week (using ISO week number)
  dailyData.forEach(item => {
    const date = new Date(item.timestamp);
    // Get the Monday of the week containing this date
    const monday = new Date(date);
    monday.setDate(date.getDate() - date.getDay() + 1);
    const weekKey = monday.toISOString().split('T')[0]; // YYYY-MM-DD format

    if (!dataByWeek[weekKey]) {
      dataByWeek[weekKey] = [];
    }
    dataByWeek[weekKey].push(item.value);
  });

  // Calculate weekly averages
  Object.keys(dataByWeek).sort().forEach(weekKey => {
    const values = dataByWeek[weekKey];
    const average = values.reduce((a, b) => a + b, 0) / values.length;
    const monday = new Date(weekKey);
    const weekLabel = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    weeklyData.push({
      timestamp: weekLabel,
      value: Math.round(average),
    });
  });

  return weeklyData;
};

const customTickFormatter = (value: string, index: number, data: any[]) => {
  if (index === 0) {
    return "1 year ago";
  } else if (index === data.length - 1) {
    return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return "";
};

export default function FearGreed({ height = 300 }: FearGreedProps) {
  const [data, setData] = useState<FearGreedData[]>([]);
  const [notification, setNotification] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = async () => {
    try {
      const res = await fetch("https://api.alternative.me/fng/?limit=365");
      const json = await res.json();

      const formatted: FearGreedData[] = json.data.map((item: any) => ({
        timestamp: new Date(parseInt(item.timestamp) * 1000).toISOString(),
        value: parseInt(item.value, 10),
      }));

      // Aggregate daily data to weekly averages
      const weeklyData = aggregateToWeekly(formatted);

      // Calculate Bollinger bands before setting data
      calculateBollingerBands(weeklyData, 6);

      setData(weeklyData);
      setLastUpdated(new Date());

      const latest = weeklyData[weeklyData.length - 1];

      let status = "";
      if (latest.value <= 20) {
        status = "😨 Extreme Fear";
      } else if (latest.value >= 80) {
        status = "🚀 Extreme Greed";
      } else {
        status = "😐 Neutral Zone";
      }

      setNotification(status);

      // Log to Firestore & Telegram only for extremes
      if (latest.value <= 20 || latest.value >= 80) {
        const logEntry = {
          value: latest.value,
          label: status,
          symbol: "FNG",
          timestamp: new Date(),
        };

        try {
          await addDoc(collection(db, "FearGreedEvents"), logEntry);
        } catch (error) {
          console.error("Failed to log FearGreed event:", error);
        }
        // Telegram notifications disabled
        // sendTelegramSignal(
        //   `📊 Fear & Greed: ${latest.value} — ${status}`,
        //   `fg_${latest.value <= 20 ? "fear" : "greed"}`
        // );

        console.log("📈 Logged F&G Event:", logEntry);
      }
    } catch (err) {
      console.error("Error fetching Fear & Greed Index:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="w-full bg-gray-800 shadow-lg rounded-lg p-3 border border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white font-bold text-sm">Fear & Greed Index</span>
        <div className="text-gray-400 text-xs">
          {lastUpdated && `Updated: ${lastUpdated.toLocaleString()}`}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col justify-center items-center min-w-[120px] sm:min-w-[120px] order-2 sm:order-1">
          {notification && (
            <div className="text-center">
              <div className="text-white font-bold text-lg mb-1">Latest</div>
              <span
                className={`text-sm font-semibold px-3 py-2 rounded block text-center ${
                  notification.includes("Extreme Fear")
                    ? "bg-red-900 text-red-200"
                    : notification.includes("Extreme Greed")
                    ? "bg-green-900 text-green-200"
                    : notification.includes("Fear")
                    ? "bg-orange-900 text-orange-200"
                    : notification.includes("Greed")
                    ? "bg-blue-900 text-blue-200"
                    : "bg-gray-700 text-gray-200"
                }`}
              >
                {notification}
              </span>
              {data.length > 0 && (
                <div className="text-white font-bold text-xl mt-2">
                  {data[data.length - 1].value}
                </div>
              )}
              <div className="text-gray-400 text-xs mt-2">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>
          )}
        </div>
        <div style={{ height: `${height}px`, flex: 1, minHeight: '200px' }} className="order-1 sm:order-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="timestamp" 
                stroke="#9CA3AF" 
                fontSize={10}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value, index) => customTickFormatter(value, index, data)}
                interval={0}
                ticks={[data[0]?.timestamp, data[data.length - 1]?.timestamp]}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={10}
                tick={{ fill: '#9CA3AF' }}
                domain={[0, 100]}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#F9FAFB'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="upper" 
                stroke="#10B981" 
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="lower" 
                stroke="#EF4444" 
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
